import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, FileCheck } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

export default function ConfidentialityPage() {
  usePageMeta("confidentiality");
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-20 md:pt-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mb-8">
            Confidentiality & Document Handling
          </h1>
          
          <div className="prose prose-zinc max-w-none font-body">
            <p className="text-zinc-600 mb-6">
              Last updated: September 2026
            </p>

            <p className="text-lg mb-8">
              Your tender documents, estimates, and quotations are commercially sensitive. We take confidentiality seriously. This page explains how we handle your documents.
            </p>

            {/* Key Principles */}
            <div className="grid sm:grid-cols-2 gap-6 mb-12 not-prose">
              <div className="p-6 border-2 border-black">
                <Shield className="mb-4" size={32} />
                <h3 className="font-heading font-bold text-lg uppercase mb-2">Strictly Confidential</h3>
                <p className="font-body text-zinc-600 text-sm">
                  All submitted materials are treated as strictly confidential business information.
                </p>
              </div>
              <div className="p-6 border-2 border-black">
                <Lock className="mb-4" size={32} />
                <h3 className="font-heading font-bold text-lg uppercase mb-2">Secure Storage</h3>
                <p className="font-body text-zinc-600 text-sm">
                  Files are stored securely and are not publicly accessible. No anonymous links.
                </p>
              </div>
              <div className="p-6 border-2 border-black">
                <Eye className="mb-4" size={32} />
                <h3 className="font-heading font-bold text-lg uppercase mb-2">Restricted Access</h3>
                <p className="font-body text-zinc-600 text-sm">
                  Only authorised reviewers access your documents for the sole purpose of providing the service.
                </p>
              </div>
              <div className="p-6 border-2 border-black">
                <FileCheck className="mb-4" size={32} />
                <h3 className="font-heading font-bold text-lg uppercase mb-2">Controlled Sharing Only</h3>
                <p className="font-body text-zinc-600 text-sm">
                  Nothing is shared with principals, clients, suppliers, or subcontractors without your instruction.
                </p>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">1. What We Receive</h2>
              <p>When you submit a tender for review, you may provide:</p>
              <ul className="list-disc pl-6">
                <li>Tender documents, specifications, and drawings lists</li>
                <li>Your current estimate or pricing</li>
                <li>Supplier and subcontractor quotations</li>
                <li>Programme or methodology documents</li>
                <li>Qualifications, assumptions, and exclusions</li>
              </ul>
              <p className="mt-4">
                We accept PDF, XLSX, DOCX, CSV, and ZIP files up to 50MB each.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">2. How We Handle Your Documents</h2>
              <ul className="list-disc pl-6">
                <li>Documents are uploaded through secure, encrypted connections</li>
                <li>Files are stored in private cloud storage, not publicly accessible URLs</li>
                <li>Access is limited to the assigned reviewer and system administrators</li>
                <li>Documents are used solely for the purpose of providing the requested review</li>
                <li>Original filenames and categories are preserved</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">3. Third-Party Service Providers and AI Tools</h2>
              <p>
                To deliver our services securely and efficiently, we use controlled third-party service providers for:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Cloud hosting and secure file storage</li>
                <li>Email delivery</li>
                <li>AI-assisted analysis tools (document parsing, calculation checking)</li>
              </ul>
              <p>
                These providers operate under contractual confidentiality obligations. They process your data only as directed by us and do not have independent rights to use or retain your information.
              </p>
              <p className="mt-4">
                <strong>AI Processing:</strong> We may use AI tools to assist with document analysis, calculation verification, or pattern recognition as part of our review process. All AI outputs are reviewed by our team before inclusion in deliverables.
              </p>
              <p className="mt-4">
                Blackline does not intentionally submit customer documents for public or general-purpose AI model training. Where supported, AI service-provider account and data-control settings are configured to prevent submitted information from being used for model training. AI-assisted processing remains subject to the applicable provider terms, privacy controls and contractual arrangements.
              </p>
              <p className="mt-4">
                <strong>Overseas Processing:</strong> Some service providers may store or process information outside New Zealand or Australia. Where overseas processing occurs, Blackline takes reasonable steps to use reputable providers with appropriate privacy, confidentiality and security protections.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">4. What We Will Not Do</h2>
              <ul className="list-disc pl-6">
                <li>Share your documents with principals, clients, suppliers, or subcontractors without your explicit instruction</li>
                <li>Contact any third party on your behalf without your direction</li>
                <li>Submit, amend, or communicate your tender to anyone</li>
                <li>Use your documents for any purpose other than providing the agreed service</li>
                <li>Make your files publicly accessible</li>
                <li>Intentionally submit your documents for public or general-purpose AI model training</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">5. Data Retention</h2>
              <p>
                <strong>Standard retention:</strong> We retain operational tender files for 90 days after report delivery. This allows time for follow-up questions and any clarifications.
              </p>
              <p className="mt-4"><strong>Exceptions:</strong></p>
              <ul className="list-disc pl-6 mb-4">
                <li>You may request earlier deletion at any time</li>
                <li>Legal, payment, or dispute requirements may require longer retention</li>
                <li>Backup copies may remain temporarily within normal secure backup cycles before automatic deletion</li>
              </ul>
              <p>
                To request early deletion, contact us at{" "}
                <a href="mailto:info@blacklineestimating.com" className="underline">info@blacklineestimating.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">6. NDA Requirements</h2>
              <p>
                If you have specific NDA or confidentiality requirements, please indicate this when submitting your tender. We can accommodate specific confidentiality arrangements where required.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading font-bold text-xl uppercase mb-4">7. Contact</h2>
              <p>
                For confidentiality-related enquiries, contact us at:{" "}
                <a href="mailto:info@blacklineestimating.com" className="underline">
                  info@blacklineestimating.com
                </a>
              </p>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-zinc-200">
            <Link to="/" className="font-mono text-sm uppercase tracking-wider hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
